import React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Tooltip from '@mui/material/Tooltip';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { toUserFacingError } from '../../utils/userFacingError';
import { MOBILE_DENSITY } from '../../theme/mobileDensity';

const UPLOAD_TOOLTIP_TEXT =
  'Attach a PDF, PNG, or JPEG (up to 20 MB) as supplemental context. ' +
  'You may also drag and drop a file.';

export interface MessageInputShellProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onAttachClick: () => void;
  /** Hide attachment controls for constrained intake surfaces. */
  showAttachments?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  error?: string | null;
  /** Remove upload chrome and outer gutters only inside a workflow sidebar. */
  minimal?: boolean;
  /** Optional icon overrides — default to AttachFileIcon and SendIcon. */
  attachIcon?: React.ReactNode;
  sendIcon?: React.ReactNode;
  /** Optional textarea ref for launcher surfaces that move focus into chat. */
  inputRef?: React.Ref<HTMLTextAreaElement>;
}

/**
 * Pure presentational chrome for a chat composer: paperclip on the left, an
 * autosizing textarea in the middle, and a Send icon button on the right.
 *
 * The shell is intentionally dumb — it does NOT call `e.preventDefault()` on
 * Enter. The consumer's `onKeyDown` handler decides Enter-vs-Shift+Enter
 * semantics and whether to prevent default.
 */
const MessageInputShell: React.FC<MessageInputShellProps> = ({
  value,
  onChange,
  onSend,
  onAttachClick,
  showAttachments = true,
  onKeyDown,
  disabled,
  placeholder = 'Ask about the FAR (RFO or Legacy)\u2026',
  error,
  attachIcon,
  sendIcon,
  inputRef,
  minimal = false,
}) => {
  const canSendText = value.trim().length > 0;
  const sendDisabled = !!disabled || !canSendText;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.5,
        px: minimal ? 0 : 1,
        py: minimal ? 0 : 1,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      {!minimal && showAttachments && <Tooltip
        title={UPLOAD_TOOLTIP_TEXT}
        enterDelay={150}
        leaveDelay={0}
        arrow
        placement="top"
        componentsProps={{
          popper: {
            sx: {
              zIndex: (theme) =>
                Math.max(theme.zIndex.tooltip, 2050) + 1,
            },
          },
          tooltip: {
            sx: {
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: 1,
              borderColor: 'divider',
              boxShadow: 2,
              maxWidth: 320,
              p: 1,
              fontSize: '0.75rem',
              '& .MuiTooltip-arrow': {
                color: 'background.paper',
                '&::before': {
                  border: 1,
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                },
              },
            },
          },
        }}
      >
        <span>
          <IconButton
            aria-label="Attach a file"
            size="small"
            disabled={!!disabled}
            onClick={onAttachClick}
          >
            {attachIcon ?? <AttachFileIcon />}
          </IconButton>
        </span>
      </Tooltip>}
      <Box
        component={TextareaAutosize}
        ref={inputRef}
        aria-label={minimal ? 'Message the workflow assistant' : undefined}
        minRows={1}
        maxRows={6}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        onKeyDown={onKeyDown}
        disabled={!!disabled}
        placeholder={placeholder}
        sx={{
          flex: 1,
          resize: 'none',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          fontFamily: 'inherit',
          fontSize: { xs: MOBILE_DENSITY.inputFontSize, sm: '0.875rem' },
          p: 1,
          outline: 'none',
          '&:focus': { borderColor: 'primary.main' },
          '&:disabled': { bgcolor: 'surfaceMuted', color: 'text.disabled' },
        }}
      />
      {minimal ? <Tooltip title="Send message"><span><IconButton
        aria-label={minimal ? 'Send message' : 'Send'}
        size="small"
        color="primary"
        disabled={sendDisabled}
        aria-disabled={sendDisabled}
        onClick={onSend}
      >
        {sendIcon ?? <SendIcon />}
      </IconButton></span></Tooltip> : <IconButton
        aria-label={minimal ? 'Send message' : 'Send'}
        size="small"
        color="primary"
        disabled={sendDisabled}
        aria-disabled={sendDisabled}
        onClick={onSend}
      >
        {sendIcon ?? <SendIcon />}
      </IconButton>}
      {error && (
        <Box
          role="alert"
          sx={{
            position: 'absolute',
            bottom: 68,
            left: 8,
            right: 8,
            fontSize: '0.75rem',
            color: 'text.primary',
            bgcolor: (theme) => alpha(theme.palette.error.light, 0.95),
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 1,
            p: 0.5,
          }}
        >
          {toUserFacingError(error)}
        </Box>
      )}
    </Box>
  );
};

export default MessageInputShell;
